import { Editor, Element, Path, Transforms } from 'slate';
import type { SlateEditor, TElement } from 'platejs';

import { ENV_BASE_URL } from 'env';
import { options } from 'common/components/ContentEditor/options';

export type TTypedElement = Element & { type: string };

const asEditor = (editor: SlateEditor | Record<string, unknown>) =>
    editor as unknown as Editor;

/** Every node type considered part of a list, kept in one place so a new list variant only needs updating here. */
const LIST_NODE_TYPES = [options.ul.type, options.ol.type, options.li.type];

export const isUrl = (text: string) => {
    try {
        const url = new URL(text);

        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
};

const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Returns the usable form of a URL held in stored article content, or
 * undefined when it is not one. Both forms occur: an absolute URL, and the
 * path older content holds, which `isUrl` rejects because `new URL` needs a
 * base to parse it.
 *
 * Resolving against the base the browser resolves the href against decides
 * the scheme with the same parser, so `/\host` or an embedded tab cannot mean
 * one thing here and another in the anchor. Only the scheme is checked — a
 * link to another domain is legitimate here.
 *
 * The trimmed value is returned rather than the original: `trim` also strips
 * whitespace the URL parser keeps, so rendering the original would resolve
 * differently from what was checked.
 */
const getSafeUrlForSchemes = (url: string, schemes: string[]) => {
    const trimmed = url.trim();

    // Resolving against a base makes any text a valid URL — `hello` becomes
    // a sibling path — so require a value that is meant as one: an explicit
    // scheme, or a path from the root.
    if (!trimmed.startsWith('/') && !SCHEME_PREFIX.test(trimmed)) return;

    try {
        const { protocol } = new URL(trimmed, document.baseURI);

        return schemes.includes(protocol) ? trimmed : undefined;
    } catch {
        return undefined;
    }
};

const CONTENT_SCHEMES = ['http:', 'https:'];

/** Schemes a link may also address: a mail client or a dialler. */
const LINK_SCHEMES = [...CONTENT_SCHEMES, 'mailto:', 'tel:'];

export const getSafeContentUrl = (url?: string) =>
    url ? getSafeUrlForSchemes(url, CONTENT_SCHEMES) : undefined;

export const getSafeLinkUrl = (url?: string) =>
    url ? getSafeUrlForSchemes(url, LINK_SCHEMES) : undefined;

/**
 * Resolves a path held in content against the host that serves files. In
 * production that is the same origin the page runs on, so this only matters
 * locally, where the backend answers on a different port than the dev server.
 * Values that already carry a scheme are left alone.
 */
export const resolveContentUrl = (url: string) =>
    url.startsWith('http') || url.startsWith('data:')
        ? url
        : `${ENV_BASE_URL}${url}`;

export const unwrapList = (editor: SlateEditor) => {
    // `match` matches both the list container (ul/ol) and its list items
    // (li) at once — they're nested inside one another, so the default
    // `mode: 'lowest'` would only unwrap the innermost match (li) and leave
    // the outer ul/ol in place. `mode: 'all'` unwraps every matching
    // ancestor so the paragraph is fully lifted out of the list.
    editor.tf.unwrapNodes({
        match: (n) =>
            Element.isElement(n) &&
            LIST_NODE_TYPES.includes((n as TTypedElement).type),
        mode: 'all',
        split: true,
    });
};

export const unwrapBlockquote = (editor: SlateEditor) => {
    editor.tf.unwrapNodes({
        match: (n) =>
            Element.isElement(n) &&
            (n as TTypedElement).type === options.blockquote.type,
        mode: 'all',
        split: true,
    });
};

export const toggleBlockquote = (editor: SlateEditor) => {
    if (!editor.selection) return;

    // Re-applying blockquote while already inside one would otherwise set
    // the *inner* paragraph's type to blockquote and nest a second quote
    // around it instead of doing nothing - toggle it off instead, matching
    // the list buttons.
    const [blockquoteAbove] = editor.api.nodes({
        match: { type: options.blockquote.type },
        mode: 'highest',
    });

    if (blockquoteAbove) {
        unwrapBlockquote(editor);

        return;
    }

    unwrapList(editor);

    const slateEditor = asEditor(editor);

    const isParagraph = (n: unknown) =>
        Element.isElement(n) &&
        Editor.isBlock(slateEditor, n) &&
        (n as TTypedElement).type === options.p.type;

    // Only top-level paragraphs are candidates - a paragraph nested inside
    // another block that happens to overlap the selection isn't meant to be
    // pulled into a new quote on its own.
    const paragraphPaths = [...editor.api.nodes({ match: isParagraph })]
        .map(([, path]) => path)
        .filter((path) => path.length === 1);

    // Group into contiguous runs (same parent, consecutive sibling index).
    // wrapNodes wraps the whole span between the first and last match, so
    // a selection spanning multiple paragraphs with a non-paragraph block in
    // between (e.g. a heading) would otherwise sweep that block into the
    // quote too and reorder it. Wrapping one run at a time - the same
    // approach toggleList uses - keeps each quote to only its own paragraphs.
    const runs: Path[][] = [];

    for (const path of paragraphPaths) {
        const previousRun = runs[runs.length - 1];
        const previousPath = previousRun?.[previousRun.length - 1];

        if (
            previousPath &&
            Path.equals(Path.parent(path), Path.parent(previousPath)) &&
            path[path.length - 1] === previousPath[previousPath.length - 1] + 1
        ) {
            previousRun.push(path);
        } else {
            runs.push([path]);
        }
    }

    editor.tf.withoutNormalizing(() => {
        // Process runs back-to-front so wrapping one run doesn't shift the
        // sibling indices of a run still waiting to be processed.
        for (const run of [...runs].reverse()) {
            Transforms.select(
                slateEditor,
                Editor.range(slateEditor, run[0], run[run.length - 1])
            );

            // Wrap the paragraphs rather than retyping them, so each keeps
            // its own <p> nested inside the new blockquote - every
            // blockquote elsewhere in this editor (see unwrapBlockquote's
            // callers, and the test fixtures) is a wrapper around a
            // paragraph, never a bare text container. Retyping a paragraph
            // itself into a blockquote would drop that wrapper and leave
            // the selection pointing at a node whose type just changed out
            // from under it, which is what put the cursor in the wrong
            // place.
            editor.tf.wrapNodes(
                { type: options.blockquote.type, children: [] } as TElement,
                { match: isParagraph }
            );
        }
    });
};

export const toggleList = (editor: SlateEditor, listType: string) => {
    if (!editor.selection) return;

    const [listAbove] = editor.api.nodes({
        match: { type: listType },
        mode: 'highest',
    });

    if (listAbove) {
        // See the comment in `unwrapList` — matching both listType and li
        // requires `mode: 'all'`, otherwise only the li unwraps and the
        // paragraph is left stranded as a direct (non-li) child of the list.
        editor.tf.unwrapNodes({
            match: (n) =>
                Element.isElement(n) &&
                ((n as TTypedElement).type === listType ||
                    (n as TTypedElement).type === options.li.type),
            mode: 'all',
            split: true,
        });

        return;
    }

    unwrapList(editor);

    const slateEditor = asEditor(editor);

    const isParagraph = (n: unknown) =>
        Element.isElement(n) &&
        Editor.isBlock(slateEditor, n) &&
        (n as TTypedElement).type === options.p.type;

    // Only consider top-level paragraphs. A paragraph nested inside another
    // block (e.g. a blockquote) that happens to overlap the selection isn't
    // meant to be pulled out into a new list — only the block it belongs to
    // (which itself doesn't match isParagraph) would be a candidate.
    const paragraphPaths = [...editor.api.nodes({ match: isParagraph })]
        .map(([, path]) => path)
        .filter((path) => path.length === 1);

    // Group into contiguous runs (same parent, consecutive sibling index).
    // wrapNodes wraps the whole span between the first and last match, so
    // wrapping the entire selection in one call would sweep any non-paragraph
    // block in between (e.g. a blockquote) into the list too. Wrapping one
    // run at a time keeps each list to only the paragraphs adjacent to it.
    const runs: Path[][] = [];

    for (const path of paragraphPaths) {
        const previousRun = runs[runs.length - 1];
        const previousPath = previousRun?.[previousRun.length - 1];

        if (
            previousPath &&
            Path.equals(Path.parent(path), Path.parent(previousPath)) &&
            path[path.length - 1] === previousPath[previousPath.length - 1] + 1
        ) {
            previousRun.push(path);
        } else {
            runs.push([path]);
        }
    }

    editor.tf.withoutNormalizing(() => {
        // Process runs back-to-front so wrapping one run doesn't shift the
        // sibling indices of a run still waiting to be processed.
        for (const run of [...runs].reverse()) {
            Transforms.select(
                slateEditor,
                Editor.range(slateEditor, run[0], run[run.length - 1])
            );

            // Wrap this run's paragraphs in one shared list...
            editor.tf.wrapNodes({ type: listType, children: [] } as TElement, {
                match: isParagraph,
            });

            // ...then wrap each paragraph in its own list item. wrapNodes
            // groups contiguous matching siblings into a single wrapper, so
            // without narrowing `at` to one paragraph at a time this run
            // would collapse into one <li> instead of one per paragraph.
            // `editor.selection` is transformed by the wrap above, so it
            // still resolves to this run's (now nested) paragraphs.
            const runParagraphPaths = [
                ...editor.api.nodes({ match: isParagraph }),
            ].map(([, path]) => path);

            for (const path of runParagraphPaths) {
                editor.tf.wrapNodes(
                    { type: options.li.type, children: [] } as TElement,
                    { at: path, match: isParagraph }
                );
            }
        }
    });
};

export { asEditor };
